import type { ComponentChildren } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import {
  IcoSparkles,
  IcoBook,
  IcoChat,
  IcoVideoCamera,
  IcoCalendar,
  IcoAcademicCap,
  IcoUsers,
  IcoQuestion,
  IcoUser,
  IcoSettings,
} from '@components/icons';
import { Button } from '@components/buttons';
import { SlideOver } from '@components/slide-over';
import { StudentNotifications } from 'client/courses/components/student-notifications';
import { UserMenu } from 'client/courses/components/nav/user-menu';
import { useCurrentUser } from 'client/lib/auth';
import { hasLevel } from 'shared/auth';
import { SiteNavLogo } from '@course/components/nav/site-nav-logo';
import { AIP_COURSE_ID } from 'shared/mirasee-consts';
import { rpx } from 'client/lib/rpx-client';
import { formatAccessDate } from 'client/courses/components/aip-access-date';
export { formatAccessDate };
import { CommunityNavLink } from './community-nav-link';

// The access lookup must never take down the page that carries it: My Courses
// is where every non-member is sent, and Help is where they go when something
// is broken. Fail open as a member -- that is exactly today's rendering. A
// tenant-move error still propagates, so the router can redirect the hostname.
export async function loadAipAccessState() {
  try {
    return await rpx.miraseeCommunity.getAipAccessState();
  } catch (err: any) {
    if (err?.data?.domain) {
      throw err;
    }
    return { isMember: true, hadMembership: false };
  }
}

/**
 * AI Playground standing for a page that has no route loader to fetch it with.
 * Starts as a member so the nav matches today's rendering, then corrects. A
 * failed lookup stays a member: a transient error must not strip a paying
 * member's nav down to the visitor one.
 */
export function useAipAccessState(enabled = true) {
  const [access, setAccess] = useState<{
    isMember: boolean;
    hadMembership: boolean;
    pendingAccessDate?: string;
    isLoaded: boolean;
    // `isLoaded` is true up front when the caller disabled the lookup: nothing
    // is coming, so a caller gating render on it must not wait forever.
  }>({ isMember: true, hadMembership: false, isLoaded: !enabled });

  useEffect(() => {
    if (!enabled) {
      return;
    }
    rpx.miraseeCommunity.getAipAccessState().then(
      (state) => setAccess({ ...state, isLoaded: true }),
      // Fail open as a member: that is exactly today's rendering, and a
      // transient error must not strip a paying member's nav.
      () => setAccess((prev) => ({ ...prev, isLoaded: true })),
    );
  }, [enabled]);

  return access;
}

export type CommunitySelectedItem =
  | 'home'
  | 'modules'
  | 'discussions'
  | 'calls'
  | 'calendar'
  | 'courses'
  | 'people'
  | 'help'
  | 'account';

function NavBarContent({
  close,
  selectedItem = 'home',
  isAipMember = true,
  hadMembership = false,
  pendingAccessDate,
}: {
  close?: () => void;
  selectedItem?: CommunitySelectedItem;
  isAipMember?: boolean;
  hadMembership?: boolean;
  pendingAccessDate?: string;
}) {
  const user = useCurrentUser();

  // Everything under AI Playground is gated on an active membership, so a
  // non-member gets Courses, Help and Account plus a way back in.
  if (!isAipMember) {
    return (
      <div class="flex h-full min-h-0 flex-col w-full">
        <div class="pt-8 pb-4 px-6">
          <SiteNavLogo href="/community/courses" />
        </div>
        <nav class="flex flex-1 flex-col p-4">
          <ul role="list" class="flex flex-1 flex-col gap-y-7">
            <li>
              <ul role="list" class="-mx-2 flex flex-col gap-1">
                <CommunityNavLink
                  isSelected={selectedItem === 'courses'}
                  href="/community/courses"
                  name="Courses"
                  Icon={IcoAcademicCap}
                  onClick={close}
                />
                {pendingAccessDate ? (
                  // Paid, waiting on the cohort. Not a link -- there is nowhere
                  // to go yet -- but the sidebar should still name what they
                  // bought.
                  <li class="flex items-center gap-3 rounded-md px-2 py-2 text-sm text-gray-500 dark:text-gray-400">
                    <IcoSparkles class="size-5 shrink-0 opacity-60" />
                    <span class="min-w-0">
                      AI Playground
                      <span class="block text-xs">opens {formatAccessDate(pendingAccessDate)}</span>
                    </span>
                  </li>
                ) : (
                  <CommunityNavLink
                    isSelected={selectedItem === 'home'}
                    href="/community/join"
                    name={`${hadMembership ? 'Rejoin' : 'Join'} AI Playground`}
                    Icon={IcoSparkles}
                    onClick={close}
                  />
                )}
                <CommunityNavLink
                  isSelected={selectedItem === 'help'}
                  href="/community/help"
                  name="Help"
                  Icon={IcoQuestion}
                  onClick={close}
                />
              </ul>
            </li>
          </ul>
          <footer class="flex flex-col pt-4 pb-8 border-t border-primary w-full">
            <ul role="list" class="-mx-2 flex flex-col gap-1">
              <CommunityNavLink
                isSelected={selectedItem === 'account'}
                href="/account"
                name="Account"
                Icon={IcoUser}
              />
              {hasLevel(user, 'admin') && (
                <CommunityNavLink
                  isSelected={false}
                  href="/admin/people"
                  name="Manage Site"
                  Icon={IcoSettings}
                />
              )}
            </ul>
          </footer>
        </nav>
      </div>
    );
  }

  return (
    <div class="flex h-full min-h-0 flex-col w-full">
      <div class="pt-8 pb-4 px-6">
        <SiteNavLogo href="/community" />
      </div>
      <nav class="flex flex-1 flex-col p-4">
        <ul role="list" class="flex flex-1 flex-col gap-y-7">
          <li>
            <ul role="list" class="-mx-2 flex flex-col gap-1">
              <CommunityNavLink
                isSelected={selectedItem === 'home'}
                href="/community"
                name="AI Playground"
                Icon={IcoSparkles}
                onClick={close}
              />
              <CommunityNavLink
                isSelected={selectedItem === 'modules'}
                href={`/courses/${AIP_COURSE_ID}/lessons`}
                name="Resources"
                Icon={IcoBook}
                onClick={close}
              />
              <CommunityNavLink
                isSelected={selectedItem === 'discussions'}
                href={`/courses/${AIP_COURSE_ID}/discussions/all`}
                name="Discussions"
                Icon={IcoChat}
                onClick={close}
              />
              <CommunityNavLink
                isSelected={selectedItem === 'calls'}
                href={`/courses/${AIP_COURSE_ID}/meetings`}
                name="Calls"
                Icon={IcoVideoCamera}
                onClick={close}
              />
              <CommunityNavLink
                isSelected={selectedItem === 'calendar'}
                href={`/courses/${AIP_COURSE_ID}/calendar`}
                name="Calendar"
                Icon={IcoCalendar}
                onClick={close}
              />
              <CommunityNavLink
                isSelected={selectedItem === 'courses'}
                href="/community/courses"
                name="Courses"
                Icon={IcoAcademicCap}
                onClick={close}
              />
              <CommunityNavLink
                isSelected={selectedItem === 'people'}
                href={`/courses/${AIP_COURSE_ID}/people`}
                name="People"
                Icon={IcoUsers}
                onClick={close}
              />
              <CommunityNavLink
                isSelected={selectedItem === 'help'}
                href="/community/help"
                name="Help"
                Icon={IcoQuestion}
                onClick={close}
              />
            </ul>
          </li>
        </ul>
        <footer class="flex flex-col pt-4 pb-8 border-t border-primary w-full">
          <ul role="list" class="-mx-2 flex flex-col gap-1">
            <CommunityNavLink
              isSelected={selectedItem === 'account'}
              href="/account"
              name="Account"
              Icon={IcoUser}
            />
            {hasLevel(user, 'admin') && (
              <CommunityNavLink
                isSelected={false}
                href="/admin/people"
                name="Manage Site"
                Icon={IcoSettings}
              />
            )}
          </ul>
        </footer>
      </nav>
    </div>
  );
}

export function CommunityLayout({
  children,
  selectedItem = 'home',
  isAipMember,
  hadMembership = false,
  pendingAccessDate,
}: {
  children: ComponentChildren;
  selectedItem?: CommunitySelectedItem;
  /**
   * False for a visitor with no active AI Playground membership. Their nav
   * drops every AIP-gated destination, and the AIP notification bell is
   * hidden, since those endpoints reject them. Required, so that a page
   * reachable by a non-member cannot silently inherit the member nav.
   */
  isAipMember: boolean;
  hadMembership?: boolean;
  /** Set when they have already bought access that opens on a future date. */
  pendingAccessDate?: string;
}) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  return (
    <div class="relative isolate flex min-h-svh w-full max-lg:flex-col lg:bg-gray-100 dark:bg-gray-900">
      {isMobileMenuOpen && (
        <SlideOver
          position="left"
          contentContainerClass="mr-16"
          close={() => setIsMobileMenuOpen(false)}
        >
          <NavBarContent
            close={() => setIsMobileMenuOpen(false)}
            selectedItem={selectedItem}
            isAipMember={isAipMember}
            hadMembership={hadMembership}
            pendingAccessDate={pendingAccessDate}
          />
        </SlideOver>
      )}
      <div class="hidden lg:flex lg:sticky lg:inset-y-0 left-0 h-screen w-64">
        <NavBarContent
          selectedItem={selectedItem}
          isAipMember={isAipMember}
          hadMembership={hadMembership}
          pendingAccessDate={pendingAccessDate}
        />
      </div>
      <main class="flex flex-1 flex-col p-4 lg:min-w-0 lg:pr-2 lg:pt-2">
        <div class="grow bg-white dark:bg-gray-800 lg:shadow-sm lg:rounded-xl pb-8">
          <div class="mx-auto px-4 lg:px-8 max-w-6xl pt-6">
            <header class="flex items-center gap-4 mb-6 lg:hidden">
              <Button onClick={() => setIsMobileMenuOpen(true)}>
                <svg
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.5"
                  stroke="currentColor"
                  class="opacity-75"
                  style="width: 24px; height: 24px"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
                  />
                </svg>
              </Button>
              <h2 class="flex-1 text-lg font-semibold text-gray-900 dark:text-white">
                {isAipMember ? 'AI Playground' : 'Mirasee'}
              </h2>
              <nav class="inline-flex items-center gap-6">
                {isAipMember && (
                  <StudentNotifications course={{ id: AIP_COURSE_ID, title: 'AI Playground' }} />
                )}
                <UserMenu />
              </nav>
            </header>
            {children}
          </div>
        </div>
      </main>
    </div>
  );
}
